import { IStoreData, ITransferItemDownload } from 'src/sync';
import { LoggerService } from '../../../../core/logger.service';
import { ERROR_CODES, ErrCode } from '../../../../shared/models';
import { ZipChunkPlanItem, ZipEntryChunkRange } from '../../../models';
import { ZIP_CHUNK_MAX_RETRIES, ZIP_CHUNK_RETRY_DELAY_MS } from '../../../transfer.constants';

export interface ZipChunkReadAheadDeps {
  log: LoggerService;
  getChunk(tItem: ITransferItemDownload, offset: number, reqLength: number): Promise<IStoreData>;
  isCancelled(): boolean;
  creditProgress(bytes: number): void;
}

interface ChunkWaiter {
  resolve: () => void;
  reject: (err: unknown) => void;
}

/**
 * Fetches a precomputed plan of encrypted chunk ranges in strict index order into a bounded ring,
 * so entries can be streamed without ever materialising a whole file.
 */
export class ZipChunkReadAheadService {
  private ring: Map<number, Uint8Array[]> = new Map();
  private chunkWaiters: Map<number, ChunkWaiter> = new Map();
  private errors: Map<number, unknown> = new Map();
  private readyBytes = 0;
  private inFlightBytes = 0;
  private inFlight = 0;
  private nextDispatch = 0;
  private consumed = 0;
  private aborted = false;
  private started = false;
  private readonly entryRanges: Map<number, ZipEntryChunkRange>;

  constructor(
    private plan: ZipChunkPlanItem[],
    private tItems: ITransferItemDownload[],
    private concurrency: number,
    private readAheadBytes: number,
    private deps: ZipChunkReadAheadDeps,
  ) {
    this.entryRanges = this.indexPlanByEntry();
  }

  /** Exposed so cancelZip's existing waiter-rejection loop can unblock a parked consumer. */
  public get waiters(): Map<number, ChunkWaiter> {
    return this.chunkWaiters;
  }

  public rangeFor(entryIndex: number): ZipEntryChunkRange {
    const range = this.entryRanges.get(entryIndex);
    return range ? { ...range } : { start: 0, end: 0 };
  }

  /** One O(plan) pass up front, so rangeFor() does not rescan the plan once per entry. */
  private indexPlanByEntry(): Map<number, ZipEntryChunkRange> {
    const ranges = new Map<number, ZipEntryChunkRange>();
    this.plan.forEach((item, index): void => {
      const range = ranges.get(item.entryIndex);
      if (range) {
        range.end = index + 1;
      } else {
        ranges.set(item.entryIndex, { start: index, end: index + 1 });
      }
    });
    return ranges;
  }

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.dispatch();
  }

  /**
   * Decrypted packets for one plan index. Always throws on cancellation or terminal failure — a clean
   * return would let client-zip close a truncated entry as if it were complete.
   */
  public async take(chunkIndex: number): Promise<Uint8Array[]> {
    this.throwIfUnusable(chunkIndex);

    if (!this.ring.has(chunkIndex)) {
      await new Promise<void>((resolve, reject): void => {
        this.chunkWaiters.set(chunkIndex, { resolve, reject });
      });
      this.throwIfUnusable(chunkIndex);
    }

    const packets = this.ring.get(chunkIndex);
    this.ring.delete(chunkIndex);
    this.readyBytes -= this.sizeOf(packets);
    this.consumed++;
    this.dispatch();
    return packets;
  }

  public assertDrained(): void {
    if (this.consumed !== this.plan.length) {
      this.deps.log.error(`[E2EE Zip] stream ended after ${this.consumed}/${this.plan.length} chunks`);
      throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
    }
  }

  public abort(): void {
    this.aborted = true;
    this.ring.clear();
    this.readyBytes = 0;
    this.rejectAll(new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR));
  }

  /** The empty-ring escape guarantees forward progress on a zero budget without unbounding the ring. */
  private dispatch(): void {
    if (this.aborted || this.errors.size > 0 || this.deps.isCancelled()) {
      return;
    }
    while (
      this.nextDispatch < this.plan.length &&
      this.inFlight < this.concurrency &&
      ((this.readyBytes === 0 && this.inFlight === 0) || this.readyBytes + this.inFlightBytes < this.readAheadBytes)
    ) {
      this.fetch(this.nextDispatch++);
    }
  }

  private fetch(chunkIndex: number): void {
    const item = this.plan[chunkIndex];
    this.inFlight++;
    this.inFlightBytes += item.encLength;

    this.fetchWithRetry(item)
      .then((packets): void => {
        this.inFlight--;
        this.inFlightBytes -= item.encLength;
        if (this.aborted) {
          return;
        }
        packets.forEach((packet): void => this.deps.creditProgress(packet.byteLength));
        this.ring.set(chunkIndex, packets);
        this.readyBytes += this.sizeOf(packets);
        this.wake(chunkIndex);
        this.dispatch();
      })
      .catch((err): void => {
        this.inFlight--;
        this.inFlightBytes -= item.encLength;
        this.errors.set(chunkIndex, err);
        this.rejectAll(err);
      });
  }

  private async fetchWithRetry(item: ZipChunkPlanItem): Promise<Uint8Array[]> {
    for (let attempt = 0; ; attempt++) {
      if (this.aborted || this.deps.isCancelled()) {
        throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
      }
      try {
        const result = await this.deps.getChunk(this.tItems[item.entryIndex], item.offset, item.encLength);
        if (result.chunkLength === 0) {
          throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
        }
        return this.toPackets(result.bytearray);
      } catch (err) {
        if (attempt >= ZIP_CHUNK_MAX_RETRIES || this.aborted || this.deps.isCancelled()) {
          throw err;
        }
        this.deps.log.warn(`[E2EE Zip] retrying chunk at offset ${item.offset}`);
        await this.delay(ZIP_CHUNK_RETRY_DELAY_MS);
      }
    }
  }

  private toPackets(bytearray: IStoreData['bytearray']): Uint8Array[] {
    const raw: ArrayBufferLike[] = Array.isArray(bytearray)
      ? (bytearray as unknown as ArrayBufferLike[])
      : [bytearray as unknown as ArrayBufferLike];
    return raw.map((packet): Uint8Array => new Uint8Array(packet));
  }

  private throwIfUnusable(chunkIndex: number): void {
    if (this.errors.has(chunkIndex)) {
      throw this.errors.get(chunkIndex);
    }
    if (this.errors.size > 0) {
      throw this.errors.values().next().value;
    }
    if (this.aborted || this.deps.isCancelled()) {
      throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
    }
  }

  private wake(chunkIndex: number): void {
    const waiter = this.chunkWaiters.get(chunkIndex);
    if (waiter) {
      this.chunkWaiters.delete(chunkIndex);
      waiter.resolve();
    }
  }

  private rejectAll(err: unknown): void {
    this.chunkWaiters.forEach((waiter): void => waiter.reject(err));
    this.chunkWaiters.clear();
  }

  private sizeOf(packets: Uint8Array[]): number {
    return packets.reduce((total, packet): number => total + packet.byteLength, 0);
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve): void => {
      setTimeout(resolve, milliseconds);
    });
  }
}
